import React, {useEffect, useRef, useState} from 'react';
import './TurnOverlay.css';
import RankSkinFrame from '../RankSkinFrame/RankSkinFrame';
import {getPlayerHexColor} from '../../utils/players/getPlayerHexColor';
import {getPhaseShortName} from "../../utils/phase/getPhaseShortName";
import {getPhaseIcon} from "../../utils/phase/getPhaseIcon";
import {getGameUiPhase} from "../../utils/gameUi/getGameUiPhase";
import {getPlayerColorStyle} from "../../utils/gameUi/getPlayerColorStyle";
import {EngravedIcon} from "../gameUi/EngravedIcon";
import '../../styles/gameUi/tokens.css';
import '../gameUi/gameUi.css';
import {hexToRgbWithOpacity} from "../../utils/other/hexToRgbWithOpacity";

const TITLE = 'Ваш ход!';
// Сколько полоса стоит на месте вместе с вылетом: дальше она улетает, и заливка гаснет
export const TURN_OVERLAY_EXIT_DELAY_MS = 1150;
// Заливка прозрачнее плотной: под ней остаются видны модалки конца хода
const WASH_OPACITY = 0.58;

/**
 * Оверлей начала своего хода: заливка цвета игрока на весь экран и базальтовая полоса,
 * которая вылетает из-за левого края и улетает за правый. Полоса — у нижнего края, чтобы
 * не закрывать модалки, которые игрок еще не успел убрать к началу хода
 */
const TurnOverlay = ({phase, factionIconUrl, playerColor, skinId, onComplete}) => {
  const overlayRef = useRef(null);
  const [animationPhase, setAnimationPhase] = useState('enter');

  useEffect(() => {
    const exitTimer = setTimeout(() => {
      setAnimationPhase('exit');
    }, TURN_OVERLAY_EXIT_DELAY_MS);

    return () => clearTimeout(exitTimer);
  }, []);

  useEffect(() => {
    if (animationPhase !== 'exit' || !overlayRef.current) return;

    const el = overlayRef.current;

    const handleAnimationEnd = (e) => {
      if (e.animationName === 'turn-overlay-fade-out') {
        el.style.display = 'none';
        onComplete();
      }
    };

    el.addEventListener('animationend', handleAnimationEnd);
    return () => el.removeEventListener('animationend', handleAnimationEnd);
  }, [animationPhase, onComplete]);

  const playerHexColor = getPlayerHexColor(playerColor);
  // Коротко, как в шапке матча: «Строительство», а не «Фаза Строительства»
  const phaseName = getPhaseShortName(phase);
  const phaseIcon = getPhaseIcon(phase);

  return (
    <div
      ref={overlayRef}
      // Оверлей монтируется в body, вне корня матча — свой корень дизайн-
      // системы (game-ui), фаза и цвет игрока для эмалей
      className={`game-ui turn-overlay turn-overlay--${animationPhase}`}
      data-gui-phase={getGameUiPhase(phase, null)}
      style={{
        ...getPlayerColorStyle(playerHexColor),
        '--player-color': hexToRgbWithOpacity(playerHexColor, WASH_OPACITY),
      }}
    >
      <div className="turn-overlay-wash"/>

      <div className="turn-overlay-track">
        <span className="turn-overlay-streak turn-overlay-streak_top" aria-hidden={true}/>
        <span className="turn-overlay-streak turn-overlay-streak_middle" aria-hidden={true}/>
        <span className="turn-overlay-streak turn-overlay-streak_bottom" aria-hidden={true}/>

        <div className="turn-overlay-banner">
          <span className="turn-overlay-glow" aria-hidden={true}/>
          <span className="turn-overlay-edge turn-overlay-edge_top" aria-hidden={true}/>
          <span className="turn-overlay-edge turn-overlay-edge_bottom" aria-hidden={true}/>
          <span className="turn-overlay-sweep" aria-hidden={true}/>
          <span className="turn-overlay-flash" aria-hidden={true}/>

          <div className="turn-overlay-content">
            <div className="turn-overlay-portrait">
              <span className="turn-overlay-portrait-ring" aria-hidden={true}/>
              <img className="turn-overlay-portrait-image" src={factionIconUrl} alt={phase}/>
              {/* Рамка рейтингового образа — поверх иконки, как и везде */}
              <RankSkinFrame skinId={skinId}/>
            </div>

            <div className="turn-overlay-info">
              <h1 className="turn-overlay-title" aria-label={TITLE}>
                {[...TITLE].map((letter, index) => (
                  <span key={index} className="turn-overlay-letter" style={{'--letter-index': index}} aria-hidden={true}>
                    {letter}
                  </span>
                ))}
              </h1>

              <span className="turn-overlay-divider" aria-hidden={true}/>

              {/* Фаза — жетон цвета фазы, как шапка матча */}
              <div className="turn-overlay-phase">
                <div className="gui-plate gui-plate_phase gui-shape_hex turn-overlay-phase-token">
                  <EngravedIcon src={phaseIcon} alt={phase}/>
                </div>
                <h2 className="turn-overlay-phase-name">{phaseName}</h2>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TurnOverlay;
